import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
    X,
    Ticket,
    AlertTriangle,
    FileText,
    CheckCircle2,
    DollarSign,
    Sparkles,
    Bell,
} from "lucide-react";
import pt from "@/lib/i18n";
import { apiGet, apiPatch } from "@/lib/api";
import { formatRelative } from "@/lib/utils";
import { Skeleton } from "@/components/ui/Skeleton";

const ICONS = {
    "ticket.assigned": Ticket,
    "sla.breach": AlertTriangle,
    "document.processed": FileText,
    "service.activated": CheckCircle2,
    "invoice.paid": DollarSign,
};

const groupKey = (iso) => {
    const now = new Date();
    const d = new Date(iso);
    const diffDays = Math.floor((now - d) / 86400000);
    if (diffDays === 0) return "today";
    if (diffDays === 1) return "yesterday";
    if (diffDays < 7) return "thisWeek";
    return "older";
};

export const NotificationsPanel = ({ open, onClose, onUnreadChange }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const data = await apiGet("/notifications");
            setItems(data || []);
            const unread = (data || []).filter((n) => !n.is_read).length;
            onUnreadChange?.(unread);
        } catch (e) {
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const markAll = async () => {
        try {
            await apiPatch("/notifications/read-all");
            setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
            onUnreadChange?.(0);
        } catch (e) {
            /* ignore */
        }
    };

    const markOne = async (id) => {
        try {
            await apiPatch(`/notifications/${id}/read`);
            setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
            onUnreadChange?.(items.filter((n) => !n.is_read && n.id !== id).length);
        } catch (e) {
            /* ignore */
        }
    };

    const grouped = items.reduce((acc, n) => {
        const k = groupKey(n.created_at);
        acc[k] = acc[k] || [];
        acc[k].push(n);
        return acc;
    }, {});

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/15 z-40"
                        onClick={onClose}
                    />
                    <motion.aside
                        initial={{ x: 420 }}
                        animate={{ x: 0 }}
                        exit={{ x: 420 }}
                        transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
                        className="fixed top-0 right-0 bottom-0 z-50 flex flex-col"
                        style={{
                            width: 380,
                            background: "var(--bg-elevated)",
                            backdropFilter: "blur(28px) saturate(1.5)",
                            borderLeft: "1px solid var(--glass-border)",
                            boxShadow: "-24px 0 64px rgba(120,80,40,0.18)",
                        }}
                        data-testid="notifications-panel"
                    >
                        <div className="flex items-center justify-between px-5 py-4 border-b border-[color:var(--glass-border)]">
                            <div className="flex items-center gap-2">
                                <Sparkles size={16} className="text-[color:var(--copper)]" />
                                <h3 className="font-display text-lg text-[color:var(--slate-deep)]">
                                    {pt.notifications.title}
                                </h3>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={markAll}
                                    className="text-xs text-[color:var(--copper-dark)] hover:text-[color:var(--copper)] font-medium px-2 py-1"
                                    data-testid="mark-all-read"
                                >
                                    {pt.notifications.markAllRead}
                                </button>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="p-1.5 rounded-md hover:bg-[color:var(--copper-subtle)]"
                                    data-testid="close-notifications"
                                    aria-label={pt.common.close}
                                >
                                    <X size={16} strokeWidth={1.6} />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto px-3 py-3">
                            {loading ? (
                                <div className="space-y-2 px-2">
                                    {[1, 2, 3, 4].map((i) => (
                                        <Skeleton key={i} height="64px" borderRadius="12px" />
                                    ))}
                                </div>
                            ) : items.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-center px-6">
                                    <div className="h-16 w-16 rounded-full bg-[color:var(--copper-subtle)] grid place-items-center mb-3">
                                        <Bell size={24} className="text-[color:var(--copper)]" />
                                    </div>
                                    <p className="font-display text-lg text-[color:var(--slate-deep)]">
                                        {pt.notifications.empty}
                                    </p>
                                </div>
                            ) : (
                                ["today", "yesterday", "thisWeek", "older"].map((k) =>
                                    grouped[k] ? (
                                        <div key={k} className="mb-4">
                                            <p className="px-3 mb-1.5 font-body text-[10px] uppercase tracking-[0.18em] text-[color:var(--slate-light)]">
                                                {pt.notifications[k]}
                                            </p>
                                            <ul className="space-y-1.5">
                                                {grouped[k].map((n) => {
                                                    const Icon = ICONS[n.type] || Bell;
                                                    return (
                                                        <li
                                                            key={n.id}
                                                            className="rounded-xl p-3 cursor-pointer transition-colors hover:bg-white/40 relative"
                                                            style={{
                                                                background: n.is_read
                                                                    ? "transparent"
                                                                    : "var(--bg-surface)",
                                                                borderLeft: n.is_read
                                                                    ? "3px solid transparent"
                                                                    : "3px solid var(--copper)",
                                                            }}
                                                            onClick={() => !n.is_read && markOne(n.id)}
                                                            data-testid={`notification-${n.id}`}
                                                        >
                                                            <div className="flex gap-3">
                                                                <div className="h-9 w-9 rounded-full bg-[color:var(--copper-subtle)] grid place-items-center flex-shrink-0">
                                                                    <Icon size={16} className="text-[color:var(--copper-dark)]" />
                                                                </div>
                                                                <div className="min-w-0 flex-1">
                                                                    <p className="font-body text-sm font-semibold text-[color:var(--slate-deep)] leading-tight">
                                                                        {n.title}
                                                                    </p>
                                                                    <p className="font-body text-xs text-[color:var(--slate-mid)] mt-0.5 leading-snug">
                                                                        {n.body}
                                                                    </p>
                                                                    <p className="font-mono text-[10px] text-[color:var(--slate-light)] mt-1">
                                                                        {formatRelative(n.created_at)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    ) : null,
                                )
                            )}
                        </div>
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
};
